<template>
  <v-card :loading="useAccountStore().isAccountUsageLoading" color="background">
    <v-skeleton-loader v-if="useAccountStore().isAccountUsageLoading" type="card" />
    <v-row v-if="!useAccountStore().isAccountUsageLoading" align="stretch" class="plan-cards-row">
      <v-col cols="12" md="6" class="d-flex">
        <v-card
          :class="['plan-card mb-1 d-flex flex-column', { 'plan-card--active': !props.plan.name }]"
        >
          <v-chip v-if="!props.plan.name" class="current-plan-chip" color="success" size="small">
            Current plan
          </v-chip>
          <v-card-item class="pb-1">
            <v-card-title class="text-subtitle-1 font-weight-medium">
              {{ starterPlan.name }}
            </v-card-title>
            <div class="plan-price-row mt-3">
              <span class="plan-price">{{ starterPlan.unit_amount }}</span>
            </div>
          </v-card-item>

          <v-divider class="mx-4" />

          <v-card-text class="flex-grow-1 py-3">
            <v-list class="plan-feature-list bg-transparent" density="compact">
              <v-list-item
                class="plan-feature-item px-0"
                v-for="feature in starterPlan.features"
                :key="feature.text"
              >
                <template v-slot:prepend>
                  <v-icon
                    :icon="feature.icon"
                    :color="!props.plan.name ? 'white' : 'primary'"
                    size="small"
                    class="mr-2"
                  />
                </template>
                <v-list-item-title class="plan-feature-text">
                  {{ feature.text }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>

          <v-card-actions v-if="props.plan.name" class="px-4 pb-4 pt-0 mt-auto justify-center">
            <v-btn
              class="select-plan-button"
              color="primary"
              variant="elevated"
              :loading="useAccountStore().subscriptionCancelLinkLoading"
              @click="
                props.subscription.cancelAtPeriodEnd
                  ? showError()
                  : handlePlanSelection(PlanTypeStatus.STARTER)
              "
            >
              {{ $t("page.account.plan.selectPlan") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-for="plan in productsArray" :key="plan.id" cols="12" md="6" class="d-flex">
        <v-card
          :class="[
            'plan-card mb-1 d-flex flex-column',
            { 'plan-card--active': props.plan.name === plan.name },
          ]"
        >
          <v-chip
            v-if="props.plan.name === plan.name"
            class="current-plan-chip"
            color="success"
            size="small"
          >
            Current plan
          </v-chip>
          <v-card-item class="pb-1">
            <v-card-title class="text-subtitle-1 font-weight-medium">
              {{ plan.name }}
            </v-card-title>
            <div class="plan-price-row mt-3">
              <span v-if="isLitePlan(plan)" class="plan-price">
                {{ formatLitePlanAmount(plan) }}
                <span class="plan-price-currency">{{ formatPlanCurrency(plan) }}</span>
                / terminal / {{ formatPlanInterval(plan) }}
              </span>
              <span v-else class="plan-price">
                {{ formatPlanPrice(plan) }}
              </span>
            </div>
          </v-card-item>

          <v-divider class="mx-4" />

          <v-card-text class="flex-grow-1 py-3">
            <v-list class="plan-feature-list bg-transparent" density="compact">
              <v-list-item
                class="plan-feature-item px-0"
                v-for="feature in litePlan.features"
                :key="feature.text"
              >
                <template v-slot:prepend>
                  <v-icon
                    :icon="feature.icon"
                    :color="props.plan.name === plan.name ? 'white' : 'primary'"
                    size="small"
                    class="mr-2"
                  />
                </template>
                <v-list-item-title class="plan-feature-text">
                  {{ feature.text }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>

          <v-card-actions
            v-if="props.plan.name !== plan.name"
            class="px-4 pb-4 pt-0 mt-auto justify-center"
          >
            <v-btn
              class="select-plan-button"
              color="primary"
              variant="elevated"
              :loading="useAccountStore().subscriptionCancelLinkLoading || isLoadingSession"
              @click="
                props.subscription.cancelAtPeriodEnd ? showError() : handlePlanSelection(plan.name)
              "
            >
              {{ $t("page.account.plan.selectPlan") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
  <upgrade-plan-dialog ref="upgradeDialog" :link="customerPortalLink || {}" />
  <downgrade-plan-dialog ref="downgradeDialog" :link="customerPortalLink || {}" />
</template>

<script lang="ts" setup>
import { useAccountStore } from "@/account/stores/account";
import { onMounted, ref, computed } from "vue";
import UpgradePlanDialog from "@/account/components/account/UpgradePlanDialog.vue";
import DowngradePlanDialog from "@/account/components/account/DowngradePlanDialog.vue";
import { PlanTypeStatus } from "@/shared/enums/status.enum";
import i18n from "@/app/plugins/i18n";
import { useSnackBarStore } from "@/shared/stores/snackbar";

// Helper function to safely translate with fallback
const safeTranslate = (key: string, defaultValue: string): string => {
  const fullKey = `page.account.plan.${key}`;
  // Check if the translation exists
  const translation = i18n.global.te(fullKey) ? i18n.global.t(fullKey) : defaultValue;
  return translation;
};

const customerPortalLink = ref<{ url?: string } | undefined>(undefined);
const isLoadingSession = ref(false);
const downgradeDialog = ref<null | { open: () => void }>(null);
const upgradeDialog = ref<null | { open: () => void }>(null);

const props = defineProps({
  plan: {
    type: Object,
    default: () => ({}),
  },
  subscription: {
    type: Object,
    default: () => ({}),
  },
});

const starterPlan = ref({
  name: i18n.global.t("page.account.plan.starter"),
  features: [
    {
      text: i18n.global.t("page.account.plan.starterFeature1"),
      icon: "mdi-account",
    },
    {
      text: i18n.global.t("page.account.plan.starterFeature2"),
      icon: "mdi-currency-usd",
    },
    {
      text: i18n.global.t("page.account.plan.starterFeature3"),
      icon: "mdi-tortoise",
    },
    {
      text: i18n.global.t("page.account.plan.starterFeature4"),
      icon: "mdi-face-man",
    },
  ],
  unit_amount: i18n.global.t("page.account.plan.starterPlanFee"),
});

const litePlan = ref({
  features: [
    {
      text: i18n.global.t("page.account.plan.liteFeature1"),
      icon: "mdi-account-group",
    },
    {
      text: i18n.global.t("page.account.plan.liteFeature2"),
      icon: "mdi-currency-usd-off",
    },
    {
      text: i18n.global.t("page.account.plan.liteFeature3"),
      icon: "mdi-rabbit",
    },
    {
      text: i18n.global.t("page.account.plan.liteFeature4"),
      icon: "mdi-party-popper",
    },
  ],
});

const productsArray = computed(() => {
  const products = Object.entries(useAccountStore().products).map((e) => e[1]);
  return products;
});

interface PlanProductPrice {
  unit_amount?: number | string;
  currency?: string;
  recurring?: {
    interval?: string;
  };
}

interface PlanProduct {
  id?: string;
  name?: string;
  description?: string;
  price?: PlanProductPrice;
}

const isLitePlan = (plan: PlanProduct) => plan?.name === PlanTypeStatus.LITE;

const formatLitePlanAmount = (plan: PlanProduct) => {
  const amount = plan?.price?.unit_amount;
  const currency = formatPlanCurrency(plan);

  if (typeof amount !== "number") {
    return "-";
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount / 100);
};

const formatPlanCurrency = (plan: PlanProduct) => {
  return String(plan?.price?.currency || "USD").toUpperCase();
};

const formatPlanInterval = (plan: PlanProduct) => {
  const interval = plan?.price?.recurring?.interval;
  return interval === "month" ? "month" : interval ? safeTranslate(interval, interval) : "month";
};

const formatPlanPrice = (plan: PlanProduct) => {
  if (!plan?.price?.unit_amount) {
    return "-";
  }

  const amount = safeTranslate(String(plan.price.unit_amount), `${plan.price.unit_amount}`);
  const interval = plan.price?.recurring?.interval;
  const billingCycle =
    interval === "month" ? "month" : interval ? safeTranslate(interval, interval) : "";

  return billingCycle ? `${amount} / ${billingCycle}` : amount;
};

onMounted(() => {
  useAccountStore()
    .fetchProductOptions()
    .catch((error) => {
      error.value = error;
    });
});

const showError = () => {
  if (props.subscription.cancelAtPeriodEnd == true) {
    useSnackBarStore().toastError({
      message: i18n.global.t("page.account.alreadyDowngraded"),
    });
  }
};

const handlePlanSelection = async (planName: string) => {
  isLoadingSession.value = true;

  try {
    // If selecting the starter plan, it's a downgrade
    if (planName === PlanTypeStatus.STARTER) {
      await downgradePlan();
    } else {
      // Otherwise it's an upgrade
      await upgradePlan();
    }
  } finally {
    isLoadingSession.value = false;
  }
};

const downgradePlan = async () => {
  if (useAccountStore().subscription?.id) {
    const customerPortal = await useAccountStore().createPortalCancelLink();
    if (customerPortal?.url) window.open(customerPortal?.url, "_blank");
  }
};

const upgradePlan = async () => {
  const hasSubscription = useAccountStore().subscription?.id;

  if (hasSubscription) {
    const portal = await useAccountStore().createPortalUpdateLink();
    if (portal?.url) window.open(portal?.url, "_blank");
  } else {
    const session = await useAccountStore().createCheckoutSessionLink();
    if (session?.url) window.open(session?.url, "_blank");
  }
};

const handlePlanChange = (selectedPlan: PlanProduct) => {
  if (props.subscription.cancelAtPeriodEnd) {
    showError();
    return;
  }

  if (props.plan.name === PlanTypeStatus.STARTER && selectedPlan.name === PlanTypeStatus.LITE) {
    upgradePlan();
    return;
  }

  if (props.plan.name === PlanTypeStatus.LITE && selectedPlan.name === PlanTypeStatus.STARTER) {
    downgradePlan();
    return;
  }
};
</script>
<style scoped>
.plan-card {
  position: relative;
  width: 100%;
  min-height: 21rem;
  color: #4a2a12;
  background: #fff3e0;
  border: 1px solid rgba(var(--v-theme-primary), 0.28);
}

.plan-card--active {
  color: #ffffff;
  background: #ff8f00;
  border-color: #ff8f00;
  box-shadow: 0 4px 14px rgba(255, 143, 0, 0.24);
}

.plan-card--active :deep(.text-medium-emphasis) {
  color: rgba(255, 255, 255, 0.82) !important;
}

.plan-card--active :deep(.v-divider) {
  border-color: #ffffff !important;
  opacity: 1;
}

.select-plan-button {
  padding-left: 24px;
  padding-right: 24px;
}

.current-plan-chip {
  position: absolute;
  top: 12px;
  right: 12px;
  color: #ffffff !important;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

.plan-price-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
}

.plan-price {
  color: rgb(var(--v-theme-primary));
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
}

.plan-card--active .plan-price {
  color: #ffffff;
}

.plan-price-currency {
  color: rgba(var(--v-theme-on-surface), 0.58);
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0;
}

.plan-card--active .plan-price-currency {
  color: rgba(255, 255, 255, 0.76);
}

.plan-price-cycle {
  font-size: 0.8rem;
}

.plan-feature-list {
  font-size: 0.95rem;
}

.plan-feature-item {
  min-height: 32px;
  line-height: 1.35;
}

.plan-feature-text {
  font-size: 0.95rem;
  line-height: 1.35;
  white-space: normal;
}
</style>
